/**
 * NotificationBell — the Header's in-app inbox (Phase 2 OMN-01, plan T4).
 *
 * A bell with the unread count (hooks/useNotificationCount: polls at most
 * every three minutes, refreshes when the tab becomes visible) that opens a
 * panel of your recent notifications (GET /api/notifications): open one to
 * follow its link (and mark it read), mark one read, or mark all read.
 *
 * The inbox is your own (owner-only on the server). It holds every message
 * whose chosen channel is In-App (My Settings → Notifications) and the record
 * of one no other channel could carry. A link is followed only when it is a
 * same-origin Compass path (`/…`, never `//…` or `/\…`), whatever the server sent.
 *
 * Phone-safe: the panel is fixed to the viewport (never wider than it, 8px
 * gutters) and scrolls inside itself, so it cannot widen a 375px page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationItemView } from '@compass/contracts/notifications';
import { api } from '../../services/api';
import { notifyNotificationsChanged, useNotificationCount } from '../../hooks/useNotificationCount';
import { timeAgo } from '../../utils/timeAgo';
import { Badge, IconButton, SelectableRow, TextButton } from '../ui';

/** How many recent items the panel shows. */
export const BELL_LIST_LIMIT = 20;

/** Only a same-origin Compass path is followed: `/x`, never `//host`, `/\host`, a scheme or whitespace. */
export function isSafeInboxLink(link: string | null | undefined): link is string {
  return typeof link === 'string' && /^\/(?![/\\])\S*$/.test(link);
}

export function badgeText(count: number): string | null {
  if (count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 16.5V11a6 6 0 1 1 12 0v5.5l1.5 2H4.5l1.5-2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M10 20.5a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export function NotificationBell({ enabled = true }: { enabled?: boolean }) {
  const count = useNotificationCount(enabled);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItemView[] | null>(null);
  const [error, setError] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const label = badgeText(count);

  const load = useCallback(async () => {
    setError(false);
    try {
      setItems((await api.listNotifications(BELL_LIST_LIMIT)).data);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => { if (open) void load(); }, [open, load]);

  // Close on Escape and on a click outside the bell + panel; focus goes back to the bell (on an
  // outside click only when nothing else took it, so a click on another control keeps its focus).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); bellRef.current?.focus(); } };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setTimeout(() => { if (!document.activeElement || document.activeElement === document.body) bellRef.current?.focus(); }, 0);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  const markRead = useCallback(async (item: NotificationItemView) => {
    if (item.readAt) return;
    try {
      const updated = await api.markNotificationRead(item.id);
      setItems((prev) => prev?.map((i) => (i.id === item.id ? updated : i)) ?? prev);
      notifyNotificationsChanged();
    } catch {
      // stays unread; the next open shows the truth
    }
  }, []);

  const markAll = useCallback(async () => {
    try {
      await api.markAllNotificationsRead();
      const at = new Date().toISOString();
      setItems((prev) => prev?.map((i) => (i.readAt ? i : { ...i, readAt: at })) ?? prev);
      notifyNotificationsChanged();
    } catch {
      setError(true);
    }
  }, []);

  const openItem = useCallback((item: NotificationItemView) => {
    void markRead(item);
    if (isSafeInboxLink(item.link)) {
      setOpen(false);
      navigate(item.link);
    }
  }, [markRead, navigate]);

  const anyUnread = (items ?? []).some((i) => !i.readAt) || count > 0;

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <IconButton
        ref={bellRef}
        aria-label={label ? `Notifications: ${count} unread` : 'Notifications: none unread'}
        aria-haspopup="dialog"
        aria-expanded={open}
        tone="secondary"
        data-testid="header-notifications"
        onClick={() => setOpen((v) => !v)}
        style={{ position: 'relative', border: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}
      >
        <BellIcon />
        {label && (
          <span data-testid="header-notifications-badge" aria-hidden="true" style={{ position: 'absolute', top: -7, right: -9, pointerEvents: 'none' }}>
            <Badge tone="danger" style={{ padding: '0 5px', fontSize: 10, lineHeight: '15px' }}>{label}</Badge>
          </span>
        )}
      </IconButton>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          data-testid="notification-panel"
          style={{
            position: 'fixed',
            top: 48,
            right: 8,
            width: 360,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: 'var(--shadow)',
            zIndex: 200,
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Notifications</span>
            <TextButton tone="accent" onClick={() => void markAll()} disabled={!anyUnread} data-testid="notification-mark-all">
              Mark all read
            </TextButton>
          </div>

          <div style={{ overflowY: 'auto', minHeight: 0, maxHeight: 420 }}>
            {error && <p role="alert" style={{ margin: 0, padding: 12, fontSize: 12, color: 'var(--red)' }}>Could not load your notifications.</p>}
            {!error && items === null && <p style={{ margin: 0, padding: 12, fontSize: 12, color: 'var(--text-muted)' }}>Loading…</p>}
            {!error && items?.length === 0 && (
              <p style={{ margin: 0, padding: 16, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                Nothing here yet. Choose In-App in My Settings → Notifications to get them here.
              </p>
            )}
            {items && items.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {items.map((item) => {
                  const unread = !item.readAt;
                  const linked = isSafeInboxLink(item.link);
                  return (
                    <li key={item.id} data-testid="notification-item" data-unread={unread ? 'true' : 'false'} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border)', background: unread ? 'var(--accent-glow)' : 'transparent' }}>
                      <SelectableRow
                        selected={unread}
                        accentBar={false}
                        boldWhenSelected={false}
                        onClick={() => openItem(item)}
                        disabled={!linked && !unread}
                        aria-label={linked ? `Open: ${item.title}` : item.title}
                        style={{ flex: 1, minWidth: 0, borderBottom: 'none', background: 'transparent', padding: 0, color: 'var(--text-primary)', cursor: linked || unread ? 'pointer' : 'default', font: 'inherit' }}
                      >
                        <span style={{ display: 'block', fontSize: 13, fontWeight: unread ? 600 : 500, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{item.title}</span>
                        {item.body && (
                          <span style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginTop: 2, fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{item.body}</span>
                        )}
                        <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo(item.createdAt)}</span>
                      </SelectableRow>
                      {unread && (
                        <TextButton tone="muted" onClick={() => void markRead(item)} aria-label={`Mark read: ${item.title}`} style={{ flexShrink: 0, fontSize: 11 }}>
                          Mark read
                        </TextButton>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
